import React, { useCallback, useEffect, useRef, useState } from 'react'
import { factories, IEmbedConfiguration, models, service } from 'powerbi-client'
import { Box, Button, ThemeProvider } from '@mui/material'
import { PbiReportViewModel } from '@rsmus/ecp-userservice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { Report } from 'powerbi-client'
import { ArrowLeftIcon } from '@rsmus/myrsm-icons'
import { useNavigate } from 'react-router'
import { useSelector } from 'react-redux'
import Spinner from '../../components/forms/Spinner'
import Unavailable from '../Unavailable'
import { Styles } from '../../types'
import MainContent from '../../components/layouts/MainContent'
import api from '../../api'
import tokens from '../../styles/tokens.json'
import {
  getAdminSelectedReportId,
  getReportData,
} from '../../store/powerBI/reportSlice'
import { accessibilityTheme, normalTheme } from '../../styles/materialTheme'
import { useAppSelector } from '../../utils/hooks'
import { getHighContrast } from '../../store/userSettings/userSettingsSlice'

const MINUTES_BEFORE_EXPIRATION = 10 // 10 minutes before expiration to refresh token

interface ReportEmbed {
  reportId: string
  embedUrl: string
  accessToken: string
  tokenExpiration: string
  isDashboard: boolean
  reportDisplayName: string
}

interface ReportData {
  reportId: string
  embedUrl: string
  accessToken: string
  tokenExpiration: string
  hasAdminRole: boolean
  pbiReports: PbiReportViewModel[] | undefined
}

const defaultReportData: ReportData = {
  reportId: '',
  embedUrl: '',
  accessToken: '',
  tokenExpiration: '',
  hasAdminRole: false,
  pbiReports: [],
}

const styles: Styles = {
  backButton: (theme) => ({
    color: theme.palette.secondary.main,
    padding: 0,
    paddingLeft: '0.75rem',
  }),
  link: {
    textDecoration: 'underline',
    padding: '0 0.5rem',
    fontSize: '1rem',
  },
  content: {
    backgroundColor: tokens.colors.white,
    justifyContent: 'center',
    height: 'fit-content',
  },
  fullscreenButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingRight: '1rem',
  },
  fullscreenButton: (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '1rem',
    textTransform: 'none',
    padding: 0,
    minWidth: 0,
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    '& .MuiButton-startIcon': {
      marginRight: 1,
      color: tokens.colors.black,
    },
  }),
}

const MobileReport = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const reportData = useSelector(getReportData)
  const adminSelectedReportId = useSelector(getAdminSelectedReportId)
  const isHighContrast = useAppSelector(getHighContrast)
  const reportDiv = useRef<HTMLDivElement>(null)
  const embeddedReportRef = useRef<Report | null>(null)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [tokenExpiration, setTokenExpiration] = useState<string>('')
  const [failedToRetrieveReport, setFailedToRetrieveReport] = useState(false)
  const [failedToRetrieveNewReport, setFailedToRetrieveNewReport] =
    useState(false)
  const powerbi = new service.Service(
    factories.hpmFactory,
    factories.wpmpFactory,
    factories.routerFactory,
  )

  async function loadReport(
    embedUrl: string,
    accessToken: string,
    reportId: string,
  ) {
    if (embedUrl === '' || accessToken === '' || reportId === '') return
    const reportContainer = reportDiv.current

    if (reportContainer) {
      powerbi.reset(reportContainer)
    }

    if (reportContainer) {
      const embedConfig: IEmbedConfiguration = {
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken,
        tokenType: models.TokenType.Embed,
        settings: {
          layoutType: models.LayoutType.MobilePortrait,
          customLayout: {
            displayOption: models.DisplayOption.FitToPage,
          },
        },
      }

      const report = (await powerbi.embed(
        reportContainer,
        embedConfig,
      )) as Report
      embeddedReportRef.current = report

      report.off('loaded')
      report.on('loaded', () => console.log('Report has loaded'))
    }
  }

  // Fetch report config from API
  async function fetchReportData(
    nameOfReport: string | undefined,
  ): Promise<ReportData> {
    try {
      const response = await api.user.report_GetReportClientByName(nameOfReport)
      const data = response?.data
      return {
        reportId: data?.embedReport?.reportId ?? '',
        embedUrl: data?.embedReport?.embedUrl ?? '',
        accessToken: data?.embedReport?.accessToken ?? '',
        tokenExpiration: data?.embedReport?.tokenExpiration ?? '', // UTC
        hasAdminRole: data?.hasAdminRole ?? false,
        pbiReports: data?.pbiReports ?? [],
      }
    } catch (error) {
      setFailedToRetrieveReport(true)
      return defaultReportData
    }
  }

  async function getAdminUserReportData(
    id: number | undefined,
  ): Promise<ReportEmbed> {
    try {
      const response = await api.user.report_GetPbiEmdedReportById(id)
      const data = response?.data
      return {
        reportId: data?.reportId ?? '',
        embedUrl: data?.embedUrl ?? '',
        accessToken: data?.accessToken ?? '',
        tokenExpiration: data?.tokenExpiration ?? '', // UTC
        isDashboard: data?.isDashboard ?? false,
        reportDisplayName: data?.reportDisplayName ?? '',
      }
    } catch (error) {
      setFailedToRetrieveNewReport(true)
      return {
        reportId: '',
        embedUrl: '',
        accessToken: '',
        tokenExpiration: '',
        isDashboard: false,
        reportDisplayName: '',
      } as ReportEmbed
    }
  }

  // Refresh token and apply to embedded report
  async function updateToken() {
    const newAccessToken = await fetchReportData(reportData.reportName)
    if (
      newAccessToken.reportId.length === 0 ||
      newAccessToken.embedUrl.length === 0 ||
      newAccessToken.accessToken.length === 0
    ) {
      if (reportData.hasAdminRole) {
        setFailedToRetrieveNewReport(true)
      } else {
        setFailedToRetrieveReport(true)
      }
    }
    const reportContainer = reportDiv.current

    if (reportContainer && newAccessToken) {
      const report = powerbi.get(reportContainer)
      setTokenExpiration(newAccessToken.tokenExpiration || '')
      await report.setAccessToken(newAccessToken.accessToken || '')
    }
  }

  const handleFullscreen = () => {
    if (embeddedReportRef.current) {
      embeddedReportRef.current.fullscreen()
    }
  }

  const getReportContainerStyle = useCallback(() => {
    const style: React.CSSProperties = {}
    style.width = failedToRetrieveNewReport ? 0 : '100vw'
    style.maxWidth = 'calc(100vw - 0.5rem)'
    style.height = 'calc(100vh - 3.125rem)'
    style.borderRadius = 0

    return style
  }, [])

  // Initial fetch on mount
  useEffect(() => {
    const getReportInfo = async () => {
      setIsLoaded(false)
      setTokenExpiration(reportData.tokenExpiration)

      if (reportData.hasAdminRole) {
        const adminReportData = await getAdminUserReportData(
          adminSelectedReportId,
        )
        await loadReport(
          adminReportData.embedUrl,
          adminReportData.accessToken,
          adminReportData.reportId,
        )
      } else {
        await loadReport(
          reportData.embedUrl,
          reportData.accessToken,
          reportData.reportId,
        )
      }

      setIsLoaded(true)
    }
    getReportInfo()
  }, [])

  // Token refresh logic using setTimeout
  useEffect(() => {
    if (!tokenExpiration) return undefined

    const expirationTime = Date.parse(tokenExpiration)
    const currentTime = Date.now()
    const bufferTime = MINUTES_BEFORE_EXPIRATION * 60 * 1000
    const refreshDelay = expirationTime - currentTime - bufferTime

    if (refreshDelay <= 0) {
      updateToken()
      return undefined
    }

    const timeoutId = setTimeout(() => {
      updateToken()
    }, refreshDelay)

    const visibilityHandler = () => {
      if (!document.hidden) {
        const now = Date.now()
        if (now >= expirationTime - bufferTime) {
          updateToken()
        }
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }, [tokenExpiration])

  return (
    <ThemeProvider theme={isHighContrast ? accessibilityTheme : normalTheme}>
      <Box id="MainContent" sx={styles.content}>
        {failedToRetrieveReport && (
          <MainContent>
            <Unavailable />
          </MainContent>
        )}
        {!failedToRetrieveReport && (
          <>
            {failedToRetrieveNewReport && <Unavailable />}
            <Spinner open={!isLoaded} />
            <Box sx={{ display: 'flex' }}>
              <Button
                data-testid="Btn_Report_Back"
                onClick={() =>
                  reportData.hasAdminRole ? navigate(-1) : navigate(-2)
                }
                sx={styles.backButton}
                role="link">
                <ArrowLeftIcon width={20} />
                <Box component="span" sx={styles.link}>
                  {t('PowerBI.Report.Back')}
                </Box>
              </Button>
              {embeddedReportRef.current && (
                <Box sx={styles.fullscreenButtonContainer}>
                  <Button
                    onClick={handleFullscreen}
                    variant="text"
                    sx={styles.fullscreenButton}
                    data-testid="Btn_PowerBI_Report_Mobile_Fullscreen"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faExpand}
                        style={{ color: 'inherit' }}
                      />
                    }>
                    {t('PowerBI.Report.FullscreenButton')}
                  </Button>
                </Box>
              )}
            </Box>

            <Box sx={getReportContainerStyle()} ref={reportDiv} />
          </>
        )}
      </Box>
    </ThemeProvider>
  )
}

export default MobileReport
